import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { AxiosResponse } from 'axios'
import api from '@/store/api'
import { omit } from 'lodash'

import { Document, DocumentPresignedPost } from '@/entities/documents/utils/types'
import { ApiListResponse } from '../utils/types'

import {
  addStoreListItem,
  prepareResponseError,
  removeStoreListItem,
  updateStoreListItem,
} from '../utils/helpers'

import { useRepositoriesStore } from '@/store/repositories/index'

export const useDocumentsStore = defineStore('documents', () => {
  // INIT
  const repositoriesStore = useRepositoriesStore()

  const list = ref<Document[]>([])

  const initFlag = ref(false)

  const loading = ref(true)
  const loadingAction = ref(false)

  let abortController = new AbortController()

  const repositoryId = computed(() => repositoriesStore.getCurrentId)

  // GETTERS
  const getList = computed<Document[]>(() => list.value)

  // SETTERS

  const setList = (data: Document[]) => {
    list.value = data
  }

  // ACTIONS

  const fetch = async (): Promise<Document[]> => {
    loading.value = true
    try {
      const result = await api.get<ApiListResponse<Document[]>>('documents', {
        params: {
          repository_id: repositoryId.value,
        },
        signal: abortController.signal,
      })
      const data = result.data.data
      await setList(data)
      initFlag.value = true
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loading.value = false
    }
  }

  const fetchSingle = async (id: string): Promise<Document> => {
    try {
      const result = await api.get<Document>(`documents/${id}`)
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const store = async (document: Document): Promise<Document> => {
    loadingAction.value = true
    try {
      const result = await api.post<
        Document,
        AxiosResponse<Document & { presigned_post: DocumentPresignedPost }>,
        Partial<Document>
      >('documents', {
        ...omit(document, 'id'),
        repository_id: repositoryId.value,
      })
      const data = result.data
      if (document.file && data.presigned_post) {
        const formData = new FormData()
        Object.entries(data.presigned_post.fields).forEach(([key, value]) => {
          formData.append(key, value)
        })
        if (document.file instanceof File) {
          formData.append('file', document.file)
        } else {
          formData.append('file', document.file.get('file') as Blob)
        }
        await api.post(data.presigned_post.url, formData)
      }
      addStoreListItem<Document>(list.value, data)
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const update = async (document: Document): Promise<Document> => {
    loadingAction.value = true
    try {
      const { id } = document
      const result = await api.put<Document, AxiosResponse<Document>, Document>(
        `documents/${id}`,
        document,
      )
      const data = result.data
      updateStoreListItem<Document>(list.value, data)
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const destroy = async (id: string): Promise<void> => {
    loadingAction.value = true
    try {
      await api.delete(`documents/${id}`)
      removeStoreListItem<Document>(list.value, id)
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const cancel = () => {
    abortController.abort()
    abortController = new AbortController()
  }

  const clear = () => {
    initFlag.value = false
    list.value = []
  }

  return {
    initFlag,
    loading,
    loadingAction,

    getList,

    fetch,
    fetchSingle,

    store,
    update,
    destroy,

    cancel,
    clear,
  }
})
